import Icon from '@components/Icons';
import emblaStyles from './embla.module.scss';
import { EmblaCarouselType } from 'embla-carousel';
import { useCallback, useEffect, useState } from 'react';

/**
 * Custom hook to handle Embla Carousel arrow buttons
 *
 * @param emblaApi - The Embla Carousel API
 * @param onButtonClick - Optional function to handle button clicks
 * @returns Object with button states and click handlers
 *
 * @example
 * ```ts
 * const {
 *  prevBtnDisabled,
 *  nextBtnDisabled,
 *  onPrevButtonClick,
 *  onNextButtonClick
 * } = useCarouselArrows(emblaApi, onNavButtonClick);
 * ```
 */
const useCarouselArrows = (
    emblaApi: EmblaCarouselType | undefined,
    onButtonClick?: (emblaApi: EmblaCarouselType) => void,
): {
    prevBtnDisabled: boolean;
    nextBtnDisabled: boolean;
    onPrevButtonClick: () => void;
    onNextButtonClick: () => void;
} => {
    // State for buttons disabled properties
    const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
    const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

    // Function to handle the previous button click
    const onPrevButtonClick = useCallback(() => {
        if (!emblaApi) return;
        emblaApi.scrollPrev();
        if (onButtonClick) onButtonClick(emblaApi);
    }, [emblaApi, onButtonClick]);

    // Function to handle the next button click
    const onNextButtonClick = useCallback(() => {
        if (!emblaApi) return;
        emblaApi.scrollNext();
        if (onButtonClick) onButtonClick(emblaApi);
    }, [emblaApi, onButtonClick]);

    // Function to handle Embla Carousel select event
    const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
        setPrevBtnDisabled(!emblaApi.canScrollPrev());
        setNextBtnDisabled(!emblaApi.canScrollNext());
    }, []);

    // useEffect to set button states and event listeners
    useEffect(() => {
        if (!emblaApi) return;

        onSelect(emblaApi);
        emblaApi.on('reInit', onSelect).on('select', onSelect);
    }, [emblaApi, onSelect]);

    // Return button states and click handlers
    return {
        prevBtnDisabled,
        nextBtnDisabled,
        onPrevButtonClick,
        onNextButtonClick,
    };
};

/**
 * Arrow buttons with icons for Embla Carousels
 *
 * @param props - The component props object
 * @param props.emblaApi - The Embla Carousel API for the carousel instance
 * @param props.onNavButtonClick - function to handle carousel navigation button clicks
 * @returns The CarouselArrows component for use in Embla Carousels
 *
 * @example
 * ```tsx
 * <CarouselArrows
 *  emblaApi={emblaApi}
 *  onNavButtonClick={onNavButtonClick}
 * />
 * ```
 */
const CarouselArrows = ({
    emblaApi,
    onNavButtonClick,
}: {
    emblaApi: EmblaCarouselType | undefined;
    onNavButtonClick: (emblaApi: EmblaCarouselType) => void;
}) => {
    const {
        prevBtnDisabled,
        nextBtnDisabled,
        onPrevButtonClick,
        onNextButtonClick,
    } = useCarouselArrows(emblaApi, onNavButtonClick);

    return (
        <>
            {/* Previous button */}
            <button
                className={`${emblaStyles.embla__button} ${emblaStyles.embla__button_prev}`}
                onClick={onPrevButtonClick}
                disabled={prevBtnDisabled}
                aria-label="Previous slide"
            >
                {/* Icon component for Previous arrow */}
                <Icon
                    iconClass="nav-arrow-left"
                    externalStlyes={emblaStyles.embla__button__svg}
                />
            </button>

            {/* Next button */}
            <button
                className={`${emblaStyles.embla__button} ${emblaStyles.embla__button_next}`}
                onClick={onNextButtonClick}
                disabled={nextBtnDisabled}
                aria-label="Next slide"
            >
                {/* Icon component for Next arrow */}
                <Icon
                    iconClass="nav-arrow-right"
                    externalStlyes={emblaStyles.embla__button__svg}
                />
            </button>
        </>
    );
};

export default CarouselArrows;
